import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Fade } from 'react-reveal';

import { PhoneIcon, EnvelopeIcon, UserIcon, MapPinIcon, HashtagIcon, BuildingStorefrontIcon, PencilIcon } from '@heroicons/react/24/solid'
import SelectListCountry, { FormCard, FormCardInput } from '../../../components/FormCard'
import { api } from '../../../api'
import { selectUserInfo, updateCompany } from "../../../features/users/userInfoSlice";
import FormBtn from '../../../components/elements/Buttons/FormBtn';
import { BRANCH_OPTIONS, COMPANY_SIZE_OPTIONS, COUNTRY_CODE_PHONE, REMOTE_PRODUCT_SELECTED_KEY, SECTOR_OPTIONS, VALIDATION_SIREN } from '../../../constants';
import ToastAction from '../../../components/ToastAction';
import AutoCompleteAddress from '../../../components/AutoCompleteAddress';
import { selectorTour } from '../../../features/tour/tourSlice';


const ClientInformationForms = () => {
	const history = useHistory()
	const dispatch = useDispatch();
	const [showItems, setShowItems] = useState(false)
	const userContext = useSelector(selectUserInfo)
	const subscribe = useSelector(state => state.subscriptions.product)
	const isTourLaunched = useSelector(selectorTour)
	const code_size = +localStorage.getItem(userContext?.user?.email + 'code_size') || null
	const {
		register: registerUser,
		handleSubmit: handleSubmitUser,
		formState: { errors: errorsUserForm, isSubmitting }
	} = useForm({
		mode: 'onTouched',
		shouldFocusError: true,
		defaultValues: {
			name: userContext?.user?.name,
			family_name: userContext?.user?.family_name,
			phone_number: userContext?.user?.phone_number ?
				userContext?.user?.phone_number?.slice(code_size, userContext?.user?.phone_number?.length) : userContext?.user?.phone_number,
			email: userContext?.user?.email
		}
	});

	const defaultIndex = useMemo(() => userContext?.user?.phone_number ? COUNTRY_CODE_PHONE.findIndex(item => item.dial_code === userContext?.user?.phone_number?.slice(0, code_size)) : -1, [userContext?.user?.phone_number, code_size])

	const {
		register: registerCompany,
		handleSubmit: handleSubmitCompany,
		setValue,
		setError,
		watch,
		formState: { errors: errorsCompanyForm, isSubmitting: isCompanySubmitting }
	} = useForm({
		mode: 'onTouched',
		shouldFocusError: true,
	});
	const [selected, setSelected] = useState(COUNTRY_CODE_PHONE[defaultIndex >= 0 ? defaultIndex : 73])

	/**
	 * @type {string}
	 */
	const sectorSelected = watch('sector')

	const address = watch('address')

	const _showItems = useCallback(() => {
		setShowItems(true)
	}, [])

	const _hideItems = useCallback(() => {
		setTimeout(() => {
			setShowItems(false)
		}, 500)
	}, [])


	useEffect(() => {
		const { company } = userContext?.user
		if (company?.legal_name) {
			for (const property in company) {
				if (property === 'sector' && !SECTOR_OPTIONS.includes(company[property])) {
					setValue(property, SECTOR_OPTIONS.at(-1))
					setValue('other_sector', company[property])
					continue
				}
				// if (property === 'branch' && !BRANCH_OPTIONS.includes(company[property])) {
				// 	setValue(property, BRANCH_OPTIONS.at(-1))
				// 	setValue('other_branch', company[property])
				// 	continue
				// }
				setValue(property, company[property])
			}
		}
	}, [userContext?.user?.company_id])

	const saveCompany = useCallback(async (company) => {
		if (!company.sector) return setError('sector', { type: "required" }, true)
		if (!company.branch) return setError('branch', { type: "required" }, true)
		if (!company.size) return setError('size', { type: "required" }, true)

		const idLoading = toast.loading('Enregistrement...')
		try {
			const newCompany = {
				...company,
				sector: company.other_sector || company.sector,
			}

			if ('logo' in company && company.logo?.length) newCompany.logo = company.logo[0]
			newCompany.company_id = userContext?.user?.company_id

			await api.company().update(newCompany)
			dispatch(updateCompany({ ...userContext?.user, company: newCompany }))
			toast.custom(t =>
				<ToastAction
					toastRef={t}
					label='Yeah !!'
					description="Les informations de l'entreprise ont bien été enregistré."
					saraExpression="wink"
				/>
			)
			toast.dismiss(idLoading)

			if (localStorage.getItem(REMOTE_PRODUCT_SELECTED_KEY) || !subscribe)
				history.push('/account/forfait')
		} catch (error) {
			console.log(error);
			toast.custom(t =>
				<ToastAction
					toastRef={t}
					label='Oups !!'
					description='Nous avons rencontré un problème. Veuillez réessayer SVP.'
					saraExpression="sorry"
				/>
			)
			toast.dismiss(idLoading)
		}
	}, [userContext?.user, subscribe])


	const saveUser = useCallback(async (newUser) => {
		const idLoading = toast.loading('Enregistrement...')

		try {
			const user = await api.auth().getUser();
			const updatedInfo = { ...newUser, phone_number: `${selected.dial_code}${newUser.phone_number}` }
			await api.user().update(user, updatedInfo);
			dispatch(updateCompany({ ...userContext?.user, ...updatedInfo }))
			localStorage.setItem(newUser?.email + 'code_size', selected.dial_code.length)
			toast.custom(t =>
				<ToastAction
					toastRef={t}
					label='Yeah !!'
					description="Les informations de votre compte ont bien été enregistré."
					saraExpression="wink"
				/>
			)
			toast.dismiss(idLoading)
		} catch (error) {
			console.log(error);
			toast.custom(t =>
				<ToastAction
					toastRef={t}
					label='Oups !!'
					description='Nous avons rencontré un problème. Veuillez réessayer SVP.'
					saraExpression="sorry"
				/>
			)
			toast.dismiss(idLoading)
		}
	}, [userContext?.user, selected])


	return (
		<div className="grid-cols-1 sm:mx-auto sm:grid-cols-3 grid gap-5 w-full mt-10 max-w-screen-xl step5">
			<form onSubmit={handleSubmitUser(saveUser)} className="user-form">
				<FormCard title="Mes informations">
					<FormCardInput
						disabled={isTourLaunched}
						register={registerUser}
						label="Nom du responsable du compte"
						name="name"
						errorType={errorsUserForm?.name?.type}
						icon={<UserIcon className="w-4 h-4 text-gray-500" />}
					/>
					<FormCardInput
						disabled={isTourLaunched}
						register={registerUser}
						label="Prénom du responsable du compte"
						name="family_name"
						errorType={errorsUserForm?.family_name?.type}
						icon={<UserIcon className="w-4 h-4 text-gray-500" />}
					/>
					<div className="flex flex-col">
						<label htmlFor="phone_number" className="text-blue-900 tracking-wider text-xs font-medium ml-3 flex">
							Numéro de téléphone
						</label>
						<div className="flex items-center space-x-1">
							<SelectListCountry selected={selected} setSelected={setSelected} />
							<FormCardInput
								disabled={isTourLaunched}
								placeholder='Ex: 636363634'
								type='tel'
								register={registerUser}
								rules={{ required: true, pattern: /^[0-9]{3}[0-9]{4,6}$/im }}
								errorType={errorsUserForm?.phone_number?.type}
								name="phone_number"
								label=" "
								icon={<PhoneIcon className="w-4 h-4 text-gray-500" />}
							/>
						</div>
					</div>
					<FormCardInput
						type='email'
						register={registerUser}
						label="Email de contact"
						name="email"
						errorType={errorsUserForm?.email?.type}
						disabled
						icon={<EnvelopeIcon className="w-4 h-4 text-gray-500" />}
					/>
					<FormBtn disabled={isTourLaunched || isSubmitting} />
				</FormCard>
			</form>
			{
				userContext?.user?.admin && <form id='save_company' onSubmit={handleSubmitCompany(saveCompany)}>
					<FormCard title="Informations de l'entreprise">
						<FormCardInput
							disabled={isTourLaunched}
							register={registerCompany}
							label="Nom Legal*"
							name="legal_name"
							errorType={errorsCompanyForm?.legal_name?.type}
							icon={<BuildingStorefrontIcon
								className="w-4 h-4 text-gray-500" />}
						/>
						<div className='w-full relative'>
							<FormCardInput
								disabled={isTourLaunched}
								onFocus={_showItems}
								onBlur={_hideItems}
								register={registerCompany}
								label="Adresse du siege*"
								name="address"
								errorType={errorsCompanyForm?.address?.type}
								icon={<MapPinIcon className="w-4 h-4 text-gray-500" />}
							/>
							<AutoCompleteAddress name="address" setValue={setValue} query={address} showItems={showItems} />
						</div>
						<FormCardInput
							disabled={isTourLaunched}
							register={registerCompany}
							label="SIREN*"
							name="siren"
							rules={{ required: true, pattern: VALIDATION_SIREN }}
							errorType={errorsCompanyForm?.siren?.type}
							icon={<HashtagIcon className="w-4 h-4 text-gray-500" />}
						/>
						<FormCardInput
							disabled={isTourLaunched}
							register={registerCompany}
							rules={{ required: false }}
							label="TVA Intracommunautaire"
							name="tva"
							errorType={errorsCompanyForm?.tva?.type}
							icon={<HashtagIcon className="w-4 h-4 text-gray-500" />}
						/>
						<FormCardInput
							disabled={isTourLaunched}
							type="select"
							options={SECTOR_OPTIONS}
							register={registerCompany}
							label="Secteur d’activité*"
							name="sector"
							errorType={errorsCompanyForm?.sector?.type}
						/>
						{
							sectorSelected?.toLowerCase() === SECTOR_OPTIONS.at(-1).toLowerCase() && <Fade top>
								<FormCardInput
									disabled={isTourLaunched}
									register={registerCompany}
									placeholder="Entrez votre secteur d'activité"
									name="other_sector"
									icon={<PencilIcon className="w-4 h-4 text-gray-500" />}
								/>
							</Fade>
						}
						<FormCardInput
							disabled={isTourLaunched}
							type='select'
							options={COMPANY_SIZE_OPTIONS}
							register={registerCompany}
							label="Taille de l’entreprise*"
							name="size"
							errorType={errorsCompanyForm?.size?.type}
						/>
						<FormCardInput
							disabled={isTourLaunched}
							register={registerCompany}
							placeholder={BRANCH_OPTIONS + '...'}
							label="Branche d’activité*"
							name="branch"
							errorType={errorsCompanyForm?.branch?.type}
						/>
						{/* <div className="space-y-1 w-full">
							<label htmlFor="logo" className="text-blue-900 tracking-wider text-xs font-medium ml-3 flex">Logo de l'entreprise</label>
							<input type='file' id='logo' {...registerCompany('logo')} className="rounded-full w-full border border-teal-700 text-xs text-teal-700 px-5 py-3 file:rounded-full file:px-2 file:border-none file:bg-teal-100 file:text-teal-600 file:shadow" accept='image/png,image/jpg,image/jpeg' />
						</div> */}
						<FormBtn disabled={isTourLaunched || isCompanySubmitting} />
					</FormCard>
				</form>
			}
		</div>
	)
}

export default memo(ClientInformationForms)